import React, { useEffect, useState, useCallback } from "react";
// Import Material-UI components for layout and styling
import { Box, Grid, Typography, Button } from "@mui/material";
// Import icons for feature indicators
import CheckIcon from "@mui/icons-material/Check";
import "./Subscription.css";
// Import website configuration for images
import { websiteImages } from "../../../config/config";
// Import toast notification utility
import { notify } from "../../../components/Toast";
// Import services for fetching subscriptions and creating checkout sessions
import {
  createStripeCheckoutSession,
  getSubscriptionList,
} from "../../../services/clientWebsite";
// Import shimmer component for loading state
import { SubscriptionCardShimmer } from "../../../components/Shimmer/Shimmer";
// Import Redux hooks for state management
import { useDispatch, useSelector } from "react-redux";
// Import authentication popup component
import AuthPopup from "../../../components/AuthPopup";
// Import Stripe library for payment processing
import { loadStripe } from "@stripe/stripe-js";
// Import hook for programmatic navigation
import { useNavigate } from "react-router-dom";
// Import Redux action for toggling chat box
import { toggleChatBox } from "../../../redux/slices/uiSlice";

// Define the SubcriptionSection component
const SubcriptionSection = () => {
  // State to manage subscription list loading
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  // State to manage subscription list data
  const [subscriptionList, setSubscriptionList] = useState([]);
  // State to manage login popup visibility
  const [loginPopupOpen, setLoginPopupOpen] = useState(false);
  // State to manage Stripe checkout loading
  const [stripeloading, setStripeLoading] = useState(false);
  // State to track the plan being processed
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  // State to store pending plan for authenticated checkout
  const [pendingPlan, setPendingPlan] = useState(null);
  // Access user data from Redux store
  const user = useSelector((state) => state.auth.user);
  // Access chat enabled status from Redux store
  const chatEnabled = useSelector((state) => state.auth.chatEnabled);
  // Redux dispatch hook
  const dispatch = useDispatch();
  // Access purchased subscription from Redux store
  const purchasedSubscription = useSelector(
    (state) => state.auth.purchasedSubscription
  );
  // Access active subscription status from Redux store
  const activeSubscription = useSelector(
    (state) => state.auth.activeSubscription
  );
  // Access currency symbol from Redux store
  const { currency_symbol } = useSelector((state) => state.globalSettings);
  // Base URL from environment variables
  const baseURL = process.env.REACT_APP_BASE_URL;
  // Initialize Stripe with publishable key
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  );
  // Hook for navigation
  const navigate = useNavigate();

  // Fetch subscription list on component mount
  useEffect(() => {
    fetchSubscriptionList();
  }, []);

  // Function to fetch subscription list from API
  const fetchSubscriptionList = async () => {
    setSubscriptionLoading(true);
    try {
      const response = await getSubscriptionList();
      setSubscriptionLoading(false);
      if (response.success) {
        setSubscriptionList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching subscription list:", error);
      setSubscriptionLoading(false);
    }
  };

  // Handle checkout process for a subscription plan
  const handleCheckout = useCallback(async (plan) => {
    if (!user) {
      // Open login popup if user is not authenticated
      setLoginPopupOpen(true);
      setPendingPlan(plan);
      return;
    }

    setStripeLoading(true);
    setLoadingPlanId(plan.id);

    // Prepare data for Stripe checkout
    const data = {
      user_id: user.id,
      subscription_id: plan.id,
      currency: "AUD",
      payment_method: "stripe",
      amount: plan.price,
      // discounted_amount: 800,
      success_url: `${baseURL}/profile`,
      cancel_url: `${baseURL}/`,
      // promo_code: "WELCOME20",
      stripe_id: plan.stripe_id, // This must be from Stripe dashboard
      is_renewable: plan.is_renewable,
    };

    try {
      const stripe = await stripePromise;
      const response = await createStripeCheckoutSession(data);
      if (response.success) {
        const sessionId = response.id;
        // Redirect to Stripe checkout
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          notify.error(error.message || "Failed to redirect to checkout");
        }
      } else {
        notify.error(
          response.message || "Failed to create checkout session",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      notify("An error occurred during checkout", "error");
    } finally {
      // Reset loading state after a delay
      setTimeout(() => {
        setStripeLoading(false);
      }, 3000);
      setLoadingPlanId(null);
    }
  }, [user, baseURL, stripePromise]);

  // Handle checkout for pending plan after user logs in
  useEffect(() => {
    if (pendingPlan && user) {
      setLoginPopupOpen(true);
      handleCheckout(pendingPlan);
      setPendingPlan(null);
    }
  }, [user, handleCheckout, pendingPlan]);

  // Helper function to check if a specific plan is active
  const isPlanActive = (planId) => {
    return purchasedSubscription && 
           Array.isArray(purchasedSubscription) &&
           purchasedSubscription.includes(planId) && 
           activeSubscription === true;
  };


  // Debug: Log subscription state to help troubleshoot button display logic
  console.log("Subscription Status:", {
    purchasedSubscription,
    activeSubscription,
    chatEnabled
  });
  return (
    <>
      {/* Main container for the subscription section */}
      <div className="subcription-container" id="subscription-section">
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            backgroundImage: `url(${websiteImages.subcriptionBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            overflow: "hidden",
            paddingBottom: "80px",
          }}
        >
          <div className="container">
            {/* Section title */}
            <Box className="subscription-title">
              <Typography
                className="main-heading"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "24px", sm: "32px", md: "40px" },
                  mb: 6,
                  color: "#fff",
                  textTransform: "uppercase",
                }}
              >
                Choose Your Path — Same System, Two Options
              </Typography>
            </Box>

            {/* Subscription plans grid */}
            <Grid
              container
              spacing={4}
              style={{
                marginTop: "70px",
                display: "flex",
                justifyContent: "space-evenly",
              }}
            >
              {subscriptionLoading ? (
                // Display shimmer effect during loading
                <SubscriptionCardShimmer count={3} variant="dark" />
              ) : (
                subscriptionList.map((plan, index) => (
                  <Grid item key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                    {/* Check if this plan is active */}
                    {(() => {
                      // Determine if this subscription is active
                      const isActive = purchasedSubscription && 
                                       purchasedSubscription.includes(plan.id) && 
                                       activeSubscription;
                      console.log(`Plan ${plan.id}:`, { 
                        planId: plan.id,
                        inPurchased: purchasedSubscription?.includes(plan.id),
                        activeSubscriptionExists: !!activeSubscription,
                        isActive
                      });
                      return null;
                    })()}
                    {/* Subscription plan card */}
                    <Box
                      className={`subscription-card ${
                        plan.is_renewable ? "" : ""
                      }`}
                    >
                      {/* Recommended badge */}
                      {plan.recommended === "1" && (
                        <Box
                          className={`subscription-badge ${plan.badgeColor}`}
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#F6520A",
                            color: "#fff",
                          }}
                        >
                          best choice
                        </Box>
                      )}
                      {/* Renewable badge */}
                      {plan.is_renewable && (
                        <Box className="renewable-badge">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 576 512"
                          >
                            <path
                              fill="currentColor"
                              d="M118.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-64 64c-9.2 9.2-11.9 22.9-6.9 34.9S19.1 160 32 160h32v224c0 53 43 96 96 96h128c17.7 0 32-14.3 32-32s-14.3-32-32-32H160c-17.7 0-32-14.3-32-32V160h32c12.9 0 24.6-7.8 29.6-19.8s2.2-25.7-6.9-34.9l-64-64zm338.8 429.2c12.5 12.5 32.8 12.5 45.3 0l64-64c9.2-9.2 11.9-22.9 6.9-34.9S556.9 352 544 352h-32V128c0-53-43-96-96-96H288c-17.7 0-32 14.3-32 32s14.3 32 32 32h128c17.7 0 32 14.3 32 32v224h-32c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l64 64z"
                            />
                          </svg>{" Renewable "}
                        </Box>
                      )}
                      {/* Plan duration */}
                      <Typography
                        className="subscription-title-text"
                        sx={{
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {plan.duration === "0"
                          ? "Free Plan"
                          : `${plan.duration} Week Plan`}
                      </Typography>
                      {/* Plan name */}
                      <Typography
                        sx={{
                          fontSize: "24px",
                          fontWeight: "bold",
                          color: "#fff",
                          textAlign: "center",
                        }}
                      >
                        {plan.subscription_name}
                      </Typography>

                      {/* Plan price */}
                      <Box className="subscription-price-container">
                        <Typography
                          component="span"
                          className="subscription-currency"
                          sx={{ fontSize: "30px", fontWeight: "800" }}
                        >
                          {currency_symbol || "$"}
                        </Typography>
                        <Typography
                          component="span"
                          className="subscription-price"
                          sx={{ fontSize: "50px", fontWeight: "800" }}
                        >
                          {plan.price}
                        </Typography>
                      </Box>

                      {/* Plan description */}
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#aaa",
                          textAlign: "center",
                          mt: 1,
                          mb: 2,
                          px: 1,
                        }}
                      >
                        {plan.subscription_description}
                      </Typography>

                      {/* Plan features list */}
                      <Box
                        className="subscription-features"
                        sx={{ mt: 2, mb: 3, px: 1 }}
                      >
                        {plan.features &&
                          plan.features.map((feature, index) => (
                            <Box
                              key={index}
                              className="subscription-feature-item"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: index !== plan.features.length - 1 ? 1 : 0, // add margin except for last item
                              }}
                            >
                              <CheckIcon
                                sx={{ fontSize: 18, color: "#00C6C2", mr: 1 }}
                              />
                              <Typography
                                className="subscription-feature"
                                sx={{ color: "#fff", fontSize: "14px" }}
                              >
                                {feature}
                              </Typography>
                            </Box>
                          ))}
                      </Box>

                      {/* Action button for purchased or new plans */}
                      {/* Only show "Open Chat"/"View More" for active subscriptions */}
                      {isPlanActive(plan.id) ? (
                        <Button
                          className="subscription-button"
                          disabled={stripeloading}
                          onClick={() => {
                            if (chatEnabled) {
                              // Open chat box for purchased plan
                              dispatch(toggleChatBox(true));
                            } else {
                              // Navigate to subscription details
                              navigate("/subscription-details");
                            }
                          }}
                        >
                          {chatEnabled ? "Open Chat" : "View More"}
                        </Button>
                      ) : (
                        <Button
                          className="subscription-button"
                          disabled={stripeloading}
                          onClick={() => handleCheckout(plan)}
                        >
                          {stripeloading && loadingPlanId === plan.id ? (
                            // Show loading spinner during checkout
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            "Start Now"
                          )}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>
          </div>
        </Box>
      </div>
      {/* Authentication popup for unauthenticated users */}
      <AuthPopup
        open={loginPopupOpen}
        onClose={() => setLoginPopupOpen(false)}
      />
    </>
  );
};

// Export the SubcriptionSection component
export default SubcriptionSection;